
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import ServiceItem from './ServiceItem';
import EmptyServicesState from './EmptyServicesState';
import { Service } from '@/lib/data';
import { useLanguage } from '@/hooks/useLanguage';

interface ServicesSectionProps {
  availableServices: Service[];
  selectedServices: Record<string, boolean>;
  priceOverrides: Record<string, number | null>;
  onServiceSelection: (service: Service, selected: boolean) => void;
  onPriceChange: (serviceId: string, value: string) => void;
  canSelectService: (serviceOrId: Service | string) => boolean;
  onAddService?: () => void; // Make this optional since not all use cases might need it
}

const ServicesSection: React.FC<ServicesSectionProps> = ({
  availableServices,
  selectedServices,
  priceOverrides,
  onServiceSelection,
  onPriceChange,
  canSelectService,
  onAddService
}) => {
  const { translate } = useLanguage();

  if (!availableServices || availableServices.length === 0) {
    // Pass a dummy function if onAddService is not provided
    return <EmptyServicesState handleAddService={onAddService || (() => {})} />;
  }

  // Get parent services (those without parent_service_id)
  const parentServices = availableServices.filter(s => !s.parent_service_id);
  
  // Get child services (those with parent_service_id)
  const childServices = availableServices.filter(s => s.parent_service_id);
  
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle>{translate('subscriptions.services')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {parentServices.map(service => (
            <React.Fragment key={service.service_id}>
              <ServiceItem
                service={service}
                isSelected={selectedServices[service.service_id] === true}
                canSelect={true}
                price={priceOverrides[service.service_id]}
                onSelectionChange={onServiceSelection}
                onPriceChange={onPriceChange}
              />
              
              {/* Child services */}
              <div className="pl-6 space-y-4 mt-4">
                {childServices
                  .filter(child => child.parent_service_id === service.service_id)
                  .map(childService => (
                    <ServiceItem
                      key={childService.service_id}
                      service={childService}
                      isSelected={selectedServices[childService.service_id] === true}
                      canSelect={canSelectService(childService)}
                      price={priceOverrides[childService.service_id]}
                      onSelectionChange={onServiceSelection}
                      onPriceChange={onPriceChange}
                      isChild={true}
                    />
                  ))
                }
              </div>
            </React.Fragment>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default ServicesSection;
